<template>
	<div class="p-6">
		<div class="mb-4">
			<router-link
				to="/discount-bulk"
				class="inline-flex items-center gap-2 text-gray-500 no-underline text-sm transition-colors hover:text-orange-7"
			>
				<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
				</svg>
				Back to Bulk Discounts
			</router-link>
		</div>

		<div class="flex justify-between items-start mb-6 flex-wrap gap-4">
			<div>
				<h1 class="text-2xl font-semibold text-gray-800 m-0">
					{{ loading ? 'Loading...' : `Edit: ${bulkDiscount?.name || ''}` }}
				</h1>
				<p class="text-gray-500 mt-1 mb-0">Configure discount properties and quantity tiers</p>
			</div>
			<button
				v-if="activeTab === 'properties'"
				:disabled="!hasChanges || saving"
				class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
				@click="saveChanges"
			>
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
				</svg>
				{{ saving ? 'Saving...' : 'Save Changes' }}
			</button>
		</div>

		<div v-if="loading" class="flex justify-center items-center py-12">
			<div class="w-8 h-8 border-2 border-gray-200 border-t-orange-7 rounded-full animate-spin"></div>
		</div>

		<div v-else-if="bulkDiscount" class="bg-white rounded-lg shadow-sm overflow-hidden">
			<div class="flex border-b border-gray-200">
				<button
					v-for="tab in tabs"
					:key="tab.id"
					class="py-4 px-6 text-sm font-medium border-none bg-transparent cursor-pointer transition-all relative border-b-2 -mb-px"
					:class="{
						'text-orange-7 border-orange-7': activeTab === tab.id,
						'text-gray-500 border-transparent': activeTab !== tab.id
					}"
					@click="activeTab = tab.id"
				>
					{{ tab.label }}
				</button>
			</div>

			<div v-if="activeTab === 'properties'" class="p-6">
				<div class="max-w-lg">
					<div class="mb-6">
						<label class="block text-sm font-medium text-gray-700 mb-2">ID</label>
						<input
							:value="bulkDiscount.id"
							type="text"
							disabled
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500 box-border"
						>
					</div>
					<div class="mb-6">
						<label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
						<input
							v-model="bulkDiscount.name"
							type="text"
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
						>
					</div>
					<div class="mb-6">
						<label class="block text-sm font-medium text-gray-700 mb-2">Count Type</label>
						<select
							v-model="bulkDiscount.count"
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none bg-white box-border"
						>
							<option value="pages">Pages</option>
							<option value="products">Products</option>
						</select>
					</div>
					<div class="mb-6">
						<label class="flex items-center gap-3 cursor-pointer">
							<input
								v-model="bulkDiscount.active"
								type="checkbox"
								:true-value="1"
								:false-value="0"
								class="w-5 h-5 accent-orange-7"
							>
							<div>
								<span class="text-sm font-medium text-gray-700">Active</span>
								<p class="text-xs text-gray-500 mt-0.5 mb-0">Enable this bulk discount for customers</p>
							</div>
						</label>
					</div>
				</div>
			</div>

			<div v-if="activeTab === 'quantities'" class="p-6">
				<div class="flex justify-between items-center mb-4">
					<h3 class="text-base font-semibold text-gray-700 m-0">Quantity Tiers</h3>
					<button
						class="inline-flex items-center gap-1.5 py-2 px-3 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer"
						@click="openAddQuantityRow"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
						</svg>
						Add Tier
					</button>
				</div>

				<div v-if="quantities.length === 0" class="text-center py-8 text-gray-500 bg-gray-50 rounded-md">
					<p class="m-0">No quantity tiers defined. Add a tier to get started.</p>
				</div>

				<table v-else class="w-full border-collapse">
					<thead>
						<tr class="bg-gray-50 border-b border-gray-200">
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">From</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">To</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Relative %</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Absolute</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Absolute PP</th>
							<th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="qty in quantities"
							:key="qty.id"
							class="border-b border-gray-200"
						>
							<td class="py-2 px-4">
								<input
									v-model.number="qty.from"
									type="number"
									class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
								>
							</td>
							<td class="py-2 px-4">
								<input
									v-model.number="qty.to"
									type="number"
									class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
								>
							</td>
							<td class="py-2 px-4">
								<input
									v-model.number="qty.relative"
									type="number"
									class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
								>
							</td>
							<td class="py-2 px-4">
								<input
									v-model.number="qty.absolute"
									type="number"
									class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
								>
							</td>
							<td class="py-2 px-4">
								<input
									v-model.number="qty.absolute_q"
									type="number"
									class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
								>
							</td>
							<td class="py-2 px-4 text-right">
								<div class="flex items-center justify-end gap-1">
									<button
										class="p-1.5 bg-transparent border-none text-orange-7 cursor-pointer rounded"
										title="Save tier"
										@click="saveQuantity(qty)"
									>
										<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
										</svg>
									</button>
									<button
										class="p-1.5 bg-transparent border-none text-red-500 cursor-pointer rounded"
										title="Delete tier"
										@click="confirmDeleteQuantity(qty)"
									>
										<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
										</svg>
									</button>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<div
					v-if="showAddQuantityRow"
					class="mt-4 p-4 bg-gray-50 rounded-md border border-dashed border-gray-300"
				>
					<h4 class="text-sm font-semibold text-gray-700 m-0 mb-4">Add New Tier</h4>
					<div class="flex flex-wrap gap-4 items-end">
						<div>
							<label class="block text-xs font-medium text-gray-500 mb-1">From</label>
							<input
								v-model.number="newQuantity.from"
								type="number"
								class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
							>
						</div>
						<div>
							<label class="block text-xs font-medium text-gray-500 mb-1">To</label>
							<input
								v-model.number="newQuantity.to"
								type="number"
								class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
							>
						</div>
						<div>
							<label class="block text-xs font-medium text-gray-500 mb-1">Relative %</label>
							<input
								v-model.number="newQuantity.relative"
								type="number"
								class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
							>
						</div>
						<div>
							<label class="block text-xs font-medium text-gray-500 mb-1">Absolute</label>
							<input
								v-model.number="newQuantity.absolute"
								type="number"
								class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
							>
						</div>
						<div>
							<label class="block text-xs font-medium text-gray-500 mb-1">Absolute PP</label>
							<input
								v-model.number="newQuantity.absolute_q"
								type="number"
								class="w-20 py-1.5 px-2 border border-gray-300 rounded text-sm"
							>
						</div>
						<div class="flex gap-2">
							<button
								class="py-2 px-3 bg-orange-7 text-white border-none rounded text-sm cursor-pointer"
								@click="addQuantity"
							>
								Add
							</button>
							<button
								class="py-2 px-3 bg-white border border-gray-300 rounded text-sm cursor-pointer"
								@click="showAddQuantityRow = false"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</div>

			<div v-if="activeTab === 'offerings'" class="p-6">
				<h3 class="text-base font-semibold text-gray-700 m-0 mb-4">Valid Product Groups</h3>
				<p class="text-sm text-gray-500 m-0 mb-6">
					Select the product groups that this bulk discount applies to.
				</p>

				<div v-if="productGroups.length === 0" class="text-center py-8 text-gray-500 bg-gray-50 rounded-md">
					<p class="m-0">No product groups available.</p>
				</div>

				<div v-else class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
					<label
						v-for="group in productGroups"
						:key="group.id"
						class="flex items-center gap-3 p-3 bg-gray-50 rounded-md cursor-pointer transition-colors hover:bg-orange-2"
					>
						<input
							type="checkbox"
							:value="group.id"
							v-model="selectedOfferings"
							class="w-4.5 h-4.5 accent-orange-7"
							@change="toggleOffering(group.id, $event)"
						>
						<span class="text-sm text-gray-700">{{ group.name }}</span>
					</label>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BulkModel, BulkQuantityModel, ProductGroupModel, OfferingModel } from '@sosocio/creator/interfaces';
import BulkService from 'services/BulkService';
import BulkQuantityService from 'services/BulkQuantityService';
import ProductGroupService from 'services/ProductGroupService';
import OfferingService from 'services/OfferingService';
import BulkOfferingService from 'services/BulkOfferingService';
import Swal from 'sweetalert2';

const showToast = (message: string, icon: 'success' | 'error' = 'success') => {
	Swal.fire({
		toast: true,
		position: 'top-end',
		icon,
		title: message,
		showConfirmButton: false,
		timer: 3000,
		timerProgressBar: true,
		customClass: {
			popup: 'windmill-swal-toast',
		},
	});
};

export default defineComponent({
	name: 'EditBulkDiscountPage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		
		const bulkDiscount = ref<BulkModel | null>(null);
		const originalBulkDiscount = ref<BulkModel | null>(null);
		const quantities = ref<BulkQuantityModel[]>([]);
		const productGroups = ref<ProductGroupModel[]>([]);
		const offerings = ref<OfferingModel[]>([]);
		const selectedOfferings = ref<number[]>([]);
		const loading = ref(false);
		const saving = ref(false);
		const activeTab = ref('properties');
		const showAddQuantityRow = ref(false);
		
		const newQuantity = ref({
			from: 0,
			to: 0,
			relative: 0,
			absolute: 0,
			absolute_q: 0,
		});
		
		const tabs = [
			{ id: 'properties', label: 'Properties' },
			{ id: 'quantities', label: 'Quantity Tiers' },
			{ id: 'offerings', label: 'Valid Offerings' },
		];
		
		const hasChanges = computed(() => {
			if (!bulkDiscount.value || !originalBulkDiscount.value) return false;
			return JSON.stringify(bulkDiscount.value) !== JSON.stringify(originalBulkDiscount.value);
		});
		
		const fetchData = async () => {
			loading.value = true;
			try {
				const id = route.params.id as string;
				const params = new URLSearchParams({ limit: '0' });
				
				const [discountData, quantitiesData, groupsData, offeringsData] = await Promise.all([
					BulkService.get(id),
					BulkService.getAllBulkQuantity(Number(id), params),
					ProductGroupService.getAll(params),
					OfferingService.getAll(params),
				]);
				
				bulkDiscount.value = discountData;
				originalBulkDiscount.value = { ...discountData };
				quantities.value = quantitiesData;
				productGroups.value = groupsData;
				offerings.value = offeringsData;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load bulk discount',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				}).then(() => {
					router.push('/discount-bulk');
				});
			} finally {
				loading.value = false;
			}
		};
		
		const saveChanges = async () => {
			if (!bulkDiscount.value || !hasChanges.value) return;
			
			saving.value = true;
			try {
				await BulkService.update(bulkDiscount.value.id, {
					name: bulkDiscount.value.name,
					count: bulkDiscount.value.count,
					active: bulkDiscount.value.active,
				});
				originalBulkDiscount.value = { ...bulkDiscount.value };
				showToast('Bulk discount updated successfully');
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to update bulk discount',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				saving.value = false;
			}
		};
		
		const openAddQuantityRow = () => {
			newQuantity.value = {
				from: 0,
				to: 0,
				relative: 0,
				absolute: 0,
				absolute_q: 0,
			};
			showAddQuantityRow.value = true;
		};
		
		const addQuantity = async () => {
			try {
				const id = route.params.id as string;
				const created = await BulkQuantityService.create({
					...newQuantity.value,
					bulkid: Number(id),
				});
				quantities.value.push(created);
				showAddQuantityRow.value = false;
				showToast('Quantity tier added successfully');
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to add quantity tier',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			}
		};
		
		const saveQuantity = async (qty: BulkQuantityModel) => {
			try {
				const id = route.params.id as string;
				await BulkQuantityService.update(qty.id, {
					from: qty.from,
					to: qty.to,
					relative: qty.relative,
					absolute: qty.absolute,
					absolute_q: qty.absolute_q,
					bulkid: Number(id),
				});
				showToast('Quantity tier saved successfully');
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save quantity tier',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			}
		};
		
		const confirmDeleteQuantity = (qty: BulkQuantityModel) => {
			Swal.fire({
				title: 'Delete Quantity Tier?',
				text: 'Are you sure you want to delete this tier? This action cannot be undone.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel',
				confirmButtonColor: '#ef4444',
				customClass: {
					popup: 'windmill-swal-popup',
					confirmButton: 'windmill-swal-confirm-danger',
					cancelButton: 'windmill-swal-cancel',
				},
			}).then(async (result) => {
				if (result.isConfirmed) {
					try {
						await BulkQuantityService.delete(qty.id);
						quantities.value = quantities.value.filter(q => q.id !== qty.id);
						showToast('Quantity tier deleted successfully');
					} catch (error: any) {
						Swal.fire({
							title: 'Error',
							text: error.message || 'Failed to delete quantity tier',
							icon: 'error',
							customClass: {
								popup: 'windmill-swal-popup',
								confirmButton: 'windmill-swal-confirm',
							},
						});
					}
				}
			});
		};
		
		const toggleOffering = async (groupId: number, event: Event) => {
			const isChecked = (event.target as HTMLInputElement).checked;
			const matchedOfferings = offerings.value.filter(off => off.groupid === groupId);
			
			try {
				if (isChecked) {
					const offeringData = matchedOfferings.map(off => ({
						offeringid: off.id,
						groupid: off.groupid,
						variantid: off.variantid,
						id: off.id,
						typeid: off.typeid,
					}));
					await BulkOfferingService.create(offeringData);
				} else {
					const ids = matchedOfferings.map(off => off.id);
					const params = new URLSearchParams({
						where: JSON.stringify({ id: ids }),
					});
					await BulkOfferingService.deleteWhere(params);
				}
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to update offerings',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			}
		};
		
		onMounted(() => {
			fetchData();
		});
		
		return {
			bulkDiscount,
			quantities,
			productGroups,
			offerings,
			selectedOfferings,
			loading,
			saving,
			activeTab,
			tabs,
			hasChanges,
			showAddQuantityRow,
			newQuantity,
			saveChanges,
			openAddQuantityRow,
			addQuantity,
			saveQuantity,
			confirmDeleteQuantity,
			toggleOffering,
		};
	},
});
</script>

<style>
@keyframes spin {
	to { transform: rotate(360deg); }
}

.windmill-swal-popup {
	font-family: 'Inter', sans-serif !important;
	border-radius: 0.5rem !important;
}

.windmill-swal-confirm {
	background-color: #9333ea !important;
	border-radius: 0.375rem !important;
}

.windmill-swal-confirm-danger {
	background-color: #ef4444 !important;
	border-radius: 0.375rem !important;
}

.windmill-swal-cancel {
	background-color: #6b7280 !important;
	border-radius: 0.375rem !important;
}

.windmill-swal-toast {
	font-family: 'Inter', sans-serif !important;
}
</style>
